import React, { useState } from "react";
import { motion } from "framer-motion";
import { adminURL } from "../../utils/urls";
import Loader from "../loader/FetchLoader";
import TimestampDisplay from "./TimestampDisplay";
import MultiPhotos from "./MultiPhotos";

const SmallDetails = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const isApproved = user.approved;

  const handleApproval = async (approved) => {
    setLoading(true);
    try {
      await adminURL.patch(`users/${user.id}`, { approved: true });
      window.location.reload();
    } catch (error) {
      console.error("Error approving user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await adminURL.patch(`users/${user.id}`, {
        approved: false,
        meta: {
          decline_reason: declineReason,
        },
      });
      window.location.reload();
    } catch (error) {
      console.error("Error declining user:", error);
    } finally {
      setLoading(false);
      setShowDeclineReason(false);
    }
  };

  const getStatusBadge = () => {
    if (user.blocked) {
      return (
        <span className="text-sm bg-red-200 text-red-800 px-2 py-1 rounded-full">
          Rejected
        </span>
      );
    }
    if (!user.approved) {
      return (
        <span className="text-sm bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full">
          Awaiting Approval
        </span>
      );
    }
    return (
      <span className="text-sm bg-green-200 text-green-800 px-2 py-1 rounded-full">
        Approved
      </span>
    );
  };

  return (
    <section className="relative p-4 space-y-4 flex flex-col items-center">
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <Loader text="Processing..." />
        </div>
      )}

      <div className="z-0 relative">
        <h2 className="text-white text-xl text-center">{user?.name ?? ""}</h2>
        <div className="relative mx-auto mt-4 w-64 h-64">
          <img
            src={user?.photo_url || "/api/placeholder/256/256"}
            alt={user?.name || "User Profile"}
            className="w-64 h-64 object-cover rounded-full"
          />
          {user?.age && (
            <div className="absolute top-2 right-2 bg-blue-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
              {user.age}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">{getStatusBadge()}</div>

      <div className="flex justify-center space-x-4 mt-4 z-0">
        <button
          className={`bg-green-600 ${
            isApproved ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"
          } text-black font-bold py-2 px-4 rounded`}
          onClick={!isApproved ? () => handleApproval(true) : null}
          disabled={loading || isApproved}
        >
          Accept Profile
        </button>
        <motion.button
          className={`${
            !isApproved
              ? "bg-red-600 hover:bg-red-700"
              : isApproved
              ? "bg-red-600 hover:bg-red-700"
              : "bg-gray-600"
          } 
            text-white font-bold py-2 px-4 rounded border border-white`}
          disabled={loading}
          onClick={() => setShowDeclineReason(true)}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          Decline Profile
        </motion.button>
      </div>

      <div className="space-y-1 mt-4 w-full max-w-xs z-0">
        <label className="text-white">Telephone Number</label>
        <input
          type="text"
          value={user?.phone ?? ""}
          readOnly
          className="bg-gray-700 outline-none text-white shadow-sm w-full rounded-lg p-2"
        />
      </div>

      {user?.created_at && (
        <TimestampDisplay label="Created at" timestamp={user?.created_at} />
      )}

      {user?.approved_at && (
        <TimestampDisplay label="Approved at" timestamp={user?.approved_at} />
      )}

      {user?.blocked_at && (
        <TimestampDisplay label="Declined at" timestamp={user?.blocked_at} />
      )}
      {user?.photos && user?.photos.length > 0 && <MultiPhotos user={user} />}

      {showDeclineReason && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-white text-lg mb-4">Reason for Declining</h3>
            <textarea
              className="w-full h-32 p-2 bg-gray-700 text-white rounded-lg focus:outline-none"
              placeholder="Enter the reason for declining..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              disabled={loading}
            />
            <div className="flex justify-end mt-4 space-x-2">
              <button
                className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded"
                onClick={() => setShowDeclineReason(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded"
                onClick={handleDecline}
                disabled={loading || !declineReason.trim()}
              >
                Confirm
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default SmallDetails;
